import { ReactNode, useMemo, useState, useEffect } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { When } from "@/components/common/When";
import { useQrSvgContent } from "@/states/common.state";
import { PreviewMobileBox } from "./PreviewMobileBox";
import { useIsloading } from "@/states/ui.state";
import { neededPathnamesForGenerate, VARIANT } from "@/constant";
import IFThen from "@/components/common/IFThen";
import classes from "./PreviewResult.module.scss";
import { useLocale, useTranslations } from "next-intl";
import { baseUrl } from "@/services/api";
const dynamicPatterns = [/^\/solutions\/[^/]+$/, "/", "/qr-code-generator", "/qr-creator", "/qr-generator", "/create-free-qr-code", "/url-to-qr-code", "/qrfast", "/qrcodecreators"];
const isPathnameMatched = (pathname: string) => dynamicPatterns.some(pattern => typeof pattern === "string" ? pathname === pattern : pattern.test(pathname));
const PreviewResult = ({
  children,
  popupInPreview,
  TEST_VARIANT,
  qrfast = false
}: {
  children?: ReactNode;
  popupInPreview?: boolean;
  TEST_VARIANT?: VARIANT;
  qrfast?: boolean;
}) => {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("Home");
  const {
    svgContent: currentSvgContent
  } = useQrSvgContent();
  const {
    isLoadingApi: loading
  } = useIsloading();
  const [isDesktop, setIsDesktop] = useState(false);
  const [svgContent, setSvgContent] = useState(currentSvgContent);
  const defaultSvgForGenerateActive = "/home-page/guest-preview.svg";
  useEffect(() => {
    if (!loading && currentSvgContent) {
      setSvgContent(currentSvgContent);
    }
  }, [loading, currentSvgContent]);
  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1025);
    const handleResize = () => setIsDesktop(window.innerWidth >= 1025);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const renderSvg = useMemo(() => {
    const defaultSvgElement = <Image width={340} height={340} src={`${baseUrl}/editor/design/qr-code-svg?lang=${locale || "en"}`} alt="Generate svg" unoptimized priority />;
    const shouldShowDefaultSvg = neededPathnamesForGenerate.includes(pathname) || isPathnameMatched(pathname);
    return <IFThen condition={shouldShowDefaultSvg && svgContent} Then={<div dangerouslySetInnerHTML={{
      __html: svgContent
    }} />} Else={defaultSvgElement} />;
  }, [svgContent, pathname, defaultSvgForGenerateActive]);
  const loaderStyle = TEST_VARIANT === VARIANT.VariantE ? {
    width: 132,
    height: 132
  } : {
    width: 150,
    height: 150
  };
  const QR = useMemo(() => loading ? <div data-test={popupInPreview ? VARIANT.VariantE : null} className={classes.loader}>
          <Image loading="eager" src={"/home-page/loadereffect.svg"} priority alt="loader effect" {...loaderStyle} width={100} height={100} />
          <div className={classes.loaderEffect}></div>
        </div> : <div className={classes.SvgBox}>{renderSvg}</div>, [loading, renderSvg, popupInPreview]);
  return <>
      <When condition={isDesktop} data-sentry-element="When" data-sentry-source-file="PreviewResult.tsx">
        <div data-test={popupInPreview ? VARIANT.VariantE : null} className={classes.Wrapper}>
          <div className={classes.Header}>
            <span className={classes.step}>3</span>
            <p className={classes.TopTitle}>{t("downloadYourQR")}</p>
          </div>
          <div className={classes.Center}>{QR}</div>
          {children}
        </div>
      </When>
      <When condition={!isDesktop} data-sentry-element="When" data-sentry-source-file="PreviewResult.tsx">
        <PreviewMobileBox renderSvg={renderSvg} isLoading={loading} qrfast={qrfast} data-sentry-element="PreviewMobileBox" data-sentry-source-file="PreviewResult.tsx" />
      </When>
    </>;
};
export default PreviewResult;